import React from 'react';
import {
    ChallengeTeamDailyResultsSupportDto,
    ChallengeTeamMemberResultsSupportDto
} from '@api/ChallengeClient';
import { Spinner } from '@components/spinner';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Button } from '@components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@components/ui/dialog';
import { Input } from '@components/ui/input';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '@components/ui/table';
import { I18n } from '@core/i18n';
import { injectTSDI } from '@core/tsdi';
import { cn } from '@core/utils/cn';
import {
    ChallengeResultFormat,
    formatChallengeResult,
    storedValueDiffersFromDisplay,
    storedValueUnitLabel
} from './raw-value';
import { ChallengeTeamLeaderboardEntry } from './types';

interface DayDraft {
    day: string;
    value: string;
    memberCount: string;
}

interface MemberDraft {
    subscriptionId: number;
    userName: string;
    value: string;
}

export interface TeamResultSubmission {
    days?: { day: string; value: number; memberCount: number }[];
    members?: { subscriptionId: number; value: number }[];
}

interface EditTeamResultDialogProps {
    entry: ChallengeTeamLeaderboardEntry | null;
    dailyResults: ChallengeTeamDailyResultsSupportDto | null;
    memberResults: ChallengeTeamMemberResultsSupportDto | null;
    resultFormat: ChallengeResultFormat;
    loading?: boolean;
    saving?: boolean;
    onClose(): void;
    onSubmit(
        teamSubscriptionId: number,
        submission: TeamResultSubmission
    ): Promise<void>;
}

function averageOf(value: number, memberCount: number) {
    return memberCount > 0 ? value / memberCount : 0;
}

const NumberCell = ({
    value,
    step,
    className,
    onChange
}: {
    value: string;
    step: number | 'any';
    className?: string;
    onChange(value: string): void;
}) => (
    <Input
        type="number"
        min={0}
        step={step}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={cn('ml-auto h-8 text-right', className)}
    />
);

export function EditTeamResultDialog({
    entry,
    dailyResults,
    memberResults,
    resultFormat,
    loading,
    saving,
    onClose,
    onSubmit
}: EditTeamResultDialogProps) {
    const { formatShortDate } = injectTSDI(I18n);
    const [days, setDays] = React.useState<DayDraft[]>([]);
    const [members, setMembers] = React.useState<MemberDraft[]>([]);

    React.useEffect(() => {
        setDays(
            (dailyResults?.days ?? []).map((d) => ({
                day: d.day,
                value: String(d.value),
                memberCount: String(d.memberCount)
            }))
        );
    }, [dailyResults]);

    React.useEffect(() => {
        setMembers(
            (memberResults?.members ?? []).map((m) => ({
                subscriptionId: m.subscriptionId,
                userName: m.userName,
                value: String(m.value)
            }))
        );
    }, [memberResults]);

    const source = dailyResults ?? memberResults;
    const editsDays = Boolean(dailyResults);
    const showsDisplay = storedValueDiffersFromDisplay(resultFormat);

    const parsedDays = days.map((d) => ({
        day: d.day,
        value: Number(d.value),
        memberCount: Number(d.memberCount)
    }));
    const parsedMembers = members.map((m) => ({
        subscriptionId: m.subscriptionId,
        value: Number(m.value)
    }));

    const daysInvalid = parsedDays.some(
        (d, i) =>
            days[i].value.trim() === '' ||
            days[i].memberCount.trim() === '' ||
            Number.isNaN(d.value) ||
            Number.isNaN(d.memberCount) ||
            d.value < 0 ||
            d.memberCount < 0 ||
            !Number.isInteger(d.memberCount)
    );
    const membersInvalid = parsedMembers.some(
        (m, i) =>
            members[i].value.trim() === '' ||
            Number.isNaN(m.value) ||
            m.value < 0
    );
    const invalid = editsDays ? daysInvalid : membersInvalid;

    const currentResult = source?.result ?? 0;
    const nextResult = editsDays
        ? parsedDays.reduce(
              (total, d) => total + averageOf(d.value, d.memberCount),
              0
          )
        : parsedMembers.reduce((total, m) => total + m.value, 0);
    const delta = nextResult - currentResult;

    const changed = editsDays
        ? days.some((d, i) => {
              const original = dailyResults?.days[i];
              return (
                  original &&
                  (Number(d.value) !== original.value ||
                      Number(d.memberCount) !== original.memberCount)
              );
          })
        : members.some((m, i) => {
              const original = memberResults?.members[i];
              return original && Number(m.value) !== original.value;
          });

    const updateDay = (index: number, patch: Partial<DayDraft>) =>
        setDays((prev) =>
            prev.map((d, i) => (i === index ? { ...d, ...patch } : d))
        );
    const updateMember = (index: number, value: string) =>
        setMembers((prev) =>
            prev.map((m, i) => (i === index ? { ...m, value } : m))
        );

    return (
        <Dialog
            open={Boolean(entry)}
            onOpenChange={(next) => !next && onClose()}
        >
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>
                        {entry
                            ? `${editsDays ? 'Daily results' : 'Team results'} · ${
                                  entry.teamName
                              }`
                            : 'Team results'}
                    </DialogTitle>
                </DialogHeader>
                {loading || !source ? (
                    <div className="flex justify-center py-8">
                        <Spinner />
                    </div>
                ) : (
                    <div className="flex flex-col gap-4">
                        <div className="max-h-[50vh] overflow-auto rounded-md border">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>
                                            {editsDays ? 'Day' : 'Member'}
                                        </TableHead>
                                        <TableHead className="text-right">
                                            {`Value (${storedValueUnitLabel(
                                                resultFormat
                                            )})`}
                                        </TableHead>
                                        {editsDays && (
                                            <TableHead className="text-right">
                                                Members
                                            </TableHead>
                                        )}
                                        <TableHead className="text-right">
                                            {editsDays
                                                ? 'Average'
                                                : 'Displays as'}
                                        </TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {editsDays
                                        ? days.map((d, index) => (
                                              <TableRow key={d.day}>
                                                  <TableCell>
                                                      {formatShortDate(d.day)}
                                                  </TableCell>
                                                  <TableCell className="text-right">
                                                      <NumberCell
                                                          value={d.value}
                                                          step="any"
                                                          className="w-[120px]"
                                                          onChange={(value) =>
                                                              updateDay(index, {
                                                                  value
                                                              })
                                                          }
                                                      />
                                                  </TableCell>
                                                  <TableCell className="text-right">
                                                      <NumberCell
                                                          value={d.memberCount}
                                                          step={1}
                                                          className="w-[90px]"
                                                          onChange={(
                                                              memberCount
                                                          ) =>
                                                              updateDay(index, {
                                                                  memberCount
                                                              })
                                                          }
                                                      />
                                                  </TableCell>
                                                  <TableCell className="text-right">
                                                      {averageOf(
                                                          parsedDays[index]
                                                              .value,
                                                          parsedDays[index]
                                                              .memberCount
                                                      ).toFixed(2)}
                                                  </TableCell>
                                              </TableRow>
                                          ))
                                        : members.map((m, index) => (
                                              <TableRow key={m.subscriptionId}>
                                                  <TableCell>
                                                      {m.userName}
                                                  </TableCell>
                                                  <TableCell className="text-right">
                                                      <NumberCell
                                                          value={m.value}
                                                          step="any"
                                                          className="w-[120px]"
                                                          onChange={(value) =>
                                                              updateMember(
                                                                  index,
                                                                  value
                                                              )
                                                          }
                                                      />
                                                  </TableCell>
                                                  <TableCell className="text-right">
                                                      {showsDisplay
                                                          ? formatChallengeResult(
                                                                parsedMembers[
                                                                    index
                                                                ].value,
                                                                resultFormat
                                                            )
                                                          : '–'}
                                                  </TableCell>
                                              </TableRow>
                                          ))}
                                </TableBody>
                            </Table>
                        </div>

                        <div>
                            <p className="text-sm text-muted-foreground">
                                Team result
                            </p>
                            <p className="text-lg font-semibold">
                                {formatChallengeResult(
                                    currentResult,
                                    resultFormat
                                )}
                                {changed && !invalid && (
                                    <>
                                        {' → '}
                                        {formatChallengeResult(
                                            nextResult,
                                            resultFormat
                                        )}
                                        <span
                                            className={cn(
                                                'ml-2 text-sm',
                                                delta >= 0
                                                    ? 'text-success'
                                                    : 'text-destructive'
                                            )}
                                        >
                                            {`(${
                                                delta >= 0 ? '+' : ''
                                            }${delta.toFixed(2)})`}
                                        </span>
                                    </>
                                )}
                            </p>
                        </div>

                        <Alert variant="info">
                            <AlertDescription>
                                {editsDays
                                    ? `The team ranks on the sum of these daily averages. A member
                                   count you set here is recomputed once someone joins or leaves
                                   the team, or new activity lands on that day.`
                                    : `The team ranks on the sum of these member results, so editing
                                   a member here also moves their own row on the Members tab.`}
                            </AlertDescription>
                        </Alert>
                    </div>
                )}
                <DialogFooter>
                    <Button variant="ghost" onClick={onClose} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        disabled={!entry || invalid || !changed}
                        loading={saving}
                        onClick={() => {
                            if (!entry) {
                                return;
                            }
                            void onSubmit(
                                entry.teamSubscriptionId,
                                editsDays
                                    ? { days: parsedDays }
                                    : { members: parsedMembers }
                            );
                        }}
                    >
                        Save
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
